import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../services/api";
import { useAuth } from "../../services/auth";
import type { EngagementSnapshot } from "./types";

type SnapshotState = {
  snapshot: EngagementSnapshot | null;
  loading: boolean;
  error: string;
  retry: () => void;
};

export function useEngagementSnapshot(): SnapshotState {
  const { token, profile } = useAuth();
  const [snapshot, setSnapshot] = useState<EngagementSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  const endpoint = useMemo(() => {
    if (profile?.userType.toLocaleLowerCase("pt-BR") === "polo" && profile.affiliation) {
      return `/api/snapshots/engajamento?scope=polo&poloKey=${encodeURIComponent(profile.affiliation)}`;
    }
    return "/api/snapshots/engajamento";
  }, [profile?.affiliation, profile?.userType]);

  useEffect(() => {
    let active = true;
    const injectedSnapshot = import.meta.env.DEV
      ? (window as typeof window & { __ENGAJAMENTO_SNAPSHOT__?: EngagementSnapshot }).__ENGAJAMENTO_SNAPSHOT__
      : undefined;

    if (!injectedSnapshot && !token) {
      setLoading(false);
      setError("Sessão indisponível para carregar o painel.");
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError("");
    const request = injectedSnapshot
      ? Promise.resolve(injectedSnapshot)
      : apiGet<EngagementSnapshot>(endpoint, token || undefined);

    void request
      .then((payload) => {
        if (!active) return;
        if (!payload || typeof payload !== "object") throw new Error("Snapshot de engajamento não encontrado.");
        setSnapshot(payload);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setSnapshot(null);
        setError(reason instanceof Error ? reason.message : "Falha ao carregar os dados de engajamento.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [attempt, endpoint, token]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return { snapshot, loading, error, retry };
}
